import {
  Box,
  Button,
  Flex,
  FormErrorMessage,
  SlideFade,
  useDisclosure,
  useToken,
} from "@chakra-ui/react"
import { FramerBox } from "@components/animations/FramerBox"
import ContactFormControl from "@components/form/ContactFormControl"
import ContactFormLabel from "@components/form/ContactFormLabel"
import ContactInput from "@components/form/ContactInput"
import ContactSelect from "@components/form/ContactSelect"
import ContactTextArea from "@components/form/ContactTextArea"
import { ContactFormProps, FormValues } from "@definitions/types"
import contactSuccess from "@public/lottie/email-confirmation-sent.json"
import { AnimatePresence } from "framer-motion"
import {
  CountryCode,
  getExampleNumber,
  isValidPhoneNumber,
} from "libphonenumber-js"
import examples from "libphonenumber-js/examples.mobile.json"
import { replaceColor } from "lottie-colorify"
import { useRouter } from "next/router"
import { useTranslations } from "next-intl"
import { useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { BsTelephone, BsWhatsapp } from "react-icons/bs"
import Lottie from "react-lottie-player/dist/LottiePlayerLight"

const V3_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY!
const V2_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY!

const ContactForm = ({
  countryList,
  defaultCountry,
  source,
}: ContactFormProps) => {
  const t = useTranslations("contact")
  const { asPath, locale } = useRouter()

  const { isOpen, onToggle, onOpen } = useDisclosure()

  useEffect(() => {
    if (source) {
      onOpen()
    }
  }, [onOpen, source])

  const defaultPhoneTranslationKey = "phone_placeholder"

  const phoneNumber =
    asPath === "/lp/linkedin"
      ? "971506004244"
      : asPath.startsWith("/lp/")
        ? "971507106247"
        : process.env.NEXT_PUBLIC_WHATSAPP_NUMBER

  const whatsappText = asPath.startsWith("/lp/")
    ? "Hey, I saw your ad on Google, can I get more info on eMenu please?"
    : "Hey, I'm interested in eMenu, please share more details."

  const defaultValues = {
    full_name: "",
    phone: "",
    email: "",
    business_name: "",
    country: defaultCountry,
    comments: "",
    source: source || `${process.env.NEXT_PUBLIC_SITE_URL}/${locale}${asPath}`,
  }

  const PhonePlaceholderHandler = (value: string) => {
    if (value === "AE") {
      setPhonePlaceHolder(t(defaultPhoneTranslationKey))
    } else {
      const exampleNumber = getExampleNumber(
        value as CountryCode,
        examples
      )?.number
      setPhonePlaceHolder(
        exampleNumber ? exampleNumber : t(defaultPhoneTranslationKey)
      )
    }
  }

  const exampleNumber = getExampleNumber(defaultCountry, examples)?.number

  const [phonePlaceHolder, setPhonePlaceHolder] = useState<string>(
    defaultCountry === "AE"
      ? t(defaultPhoneTranslationKey)
      : exampleNumber
        ? exampleNumber
        : t(defaultPhoneTranslationKey)
  )

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: defaultValues,
  })

  const [brandPrimary] = useToken("colors", ["brand.primary"])

  const [formSuccess, setFormSuccess] = useState<boolean>(false)
  const [formLoading, setFormLoading] = useState<boolean>(false)
  const [formError, setFormError] = useState<boolean>(false)
  const [useV2, setUseV2] = useState(false)
  const widgetId = useRef<number | null>(null)

  // Load reCAPTCHA v3 script on mount
  useEffect(() => {
    const s = document.createElement("script")
    s.src = `https://www.google.com/recaptcha/api.js?render=${V3_SITE_KEY}`
    s.async = true
    document.body.appendChild(s)
  }, [])

  // When v2 fallback is needed, load the v2 script + render widget
  useEffect(() => {
    if (!useV2) return

    const renderV2 = () => {
      if (widgetId.current !== null) return
      widgetId.current = window.grecaptcha.render("recaptcha-v2", {
        sitekey: V2_SITE_KEY,
        size: "invisible",
        callback: async (token: string) => {
          // auto-submit after challenge
          const payload = {
            ...getValues(),
            captchaToken: token,
            recaptchaVersion: "v2" as const,
          }
          try {
            const r = await fetch("/api/contact", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            })
            if (r.ok) {
              setFormSuccess(true)
            } else {
              setFormError(true)
            }
          } catch {
            setFormError(true)
          }
        },
      })
      // trigger it immediately
      // @ts-expect-error grecaptcha types have 2 execute signatures
      window.grecaptcha.execute(widgetId.current!, { action: "contact_form" })
    }

    // either script is already loaded…
    // @ts-expect-error grecaptcha types issue
    if (window.grecaptcha?.render) {
      renderV2()
    } else {
      // …or inject it now
      const s2 = document.createElement("script")
      s2.src = "https://www.google.com/recaptcha/api.js"
      s2.async = true
      s2.defer = true
      s2.onload = renderV2
      document.body.appendChild(s2)
    }
  }, [useV2, getValues])

  const onSubmit = async (data: FormValues) => {
    setFormLoading(true)
    setFormError(false)

    try {
      // Attempt to use reCAPTCHA v3 first
      const token = await window.grecaptcha.execute(V3_SITE_KEY, {
        action: "contact_form",
      })

      const payload = {
        ...data,
        captchaToken: token,
        recaptchaVersion: "v3" as const,
      }
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      const json = await res.json()

      if (res.ok) {
        setFormSuccess(true)
      } else if (json.captchaRequired) {
        // server asked for v2
        setUseV2(true)
      } else {
        setFormError(true)
      }
    } catch {
      setFormError(true)
    } finally {
      setFormLoading(false)
    }
  }

  if (formSuccess) {
    return (
      <Flex
        alignItems={"center"}
        direction={"column"}
        width={"full"}
      >
        <Box width={"sm"}>
          <Lottie
            loop={false}
            play={true}
            speed={3}
            animationData={replaceColor(
              [204, 204, 204],
              brandPrimary,
              contactSuccess
            )}
          />
        </Box>

        <Box>{t("thanks_message")}</Box>
      </Flex>
    )
  }

  return (
    <AnimatePresence>
      <FramerBox
        key={"contact-form"}
        initial={{ opacity: 0, x: 0, y: 0, scale: 0 }}
        whileInView={{
          opacity: 1,
          scale: 1,
          x: 0,
          y: 0,
        }}
        exit={{ opacity: 0, x: 0, y: 0, scale: 0 }}
      >
        <Box
          as={"form"}
          onSubmit={handleSubmit(onSubmit)}
          display={"flex"}
          alignItems={{ base: "center", md: "flex-start" }}
          flexDirection={"column"}
        >
          {!source ? (
            <Flex
              w={"full"}
              justifyContent={"center"}
              mt={10}
            >
              <Button
                leftIcon={<BsWhatsapp />}
                colorScheme={"green"}
                textTransform={"uppercase"}
                fontWeight={"normal"}
                px={10}
                me={{ base: 5, lg: 10 }}
                onClick={() => {
                  window.open(
                    `https://wa.me/${phoneNumber}?text=${whatsappText}`,
                    "_blank"
                  )
                }}
              >
                {t("whatsapp")}
              </Button>
              <Button
                leftIcon={<BsTelephone />}
                colorScheme={"blue"}
                textTransform={"uppercase"}
                fontWeight={"normal"}
                px={10}
                me={{ base: 5, lg: 10 }}
                onClick={() => {
                  window.open(`tel:+${phoneNumber}`, "_blank")
                }}
              >
                {t("call_us")}
              </Button>
            </Flex>
          ) : (
            <Flex
              w={"full"}
              justifyContent={"center"}
              fontSize={"lg"}
              fontWeight={"bold"}
              mt={10}
            >
              {source} {"Form"}
            </Flex>
          )}
          <Flex
            justifyContent={"space-between"}
            wrap={"wrap"}
            w={"full"}
            mt={10}
          >
            <ContactFormControl
              label={source ? "Name" : t("hi_iam")}
              isInvalid={!!errors.full_name}
              isRequired
            >
              <Flex flexDirection={{ base: "column", md: "row" }}>
                <ContactFormLabel htmlFor={"full_name"}>
                  {source ? "Name" : t("hi_iam")}
                </ContactFormLabel>
                <ContactInput
                  control={control}
                  name={"full_name"}
                  id={"full_name"}
                  placeholder={t("john_doe")}
                  rules={{
                    required: t("full_name_error1"),
                    minLength: {
                      value: 3,
                      message: t("full_name_error2"),
                    },
                  }}
                />
              </Flex>
              <FormErrorMessage>
                {errors.full_name && errors.full_name.message}
              </FormErrorMessage>
            </ContactFormControl>

            <ContactFormControl
              label={source ? "Phone" : t("call_me")}
              isInvalid={!!errors.phone}
              isRequired
            >
              <Flex flexDirection={{ base: "column", md: "row" }}>
                <ContactFormLabel htmlFor={"phone"}>
                  {source ? "Phone" : t("call_me")}
                </ContactFormLabel>
                <ContactInput
                  control={control}
                  name={"phone"}
                  id={"phone"}
                  placeholder={phonePlaceHolder}
                  type={"tel"}
                  rules={{
                    required: t("phone_error1"),
                    validate: (value) =>
                      (value && isValidPhoneNumber(value, defaultCountry)) ||
                      t("phone_error2"),
                  }}
                />
              </Flex>
              <FormErrorMessage>
                {errors.phone && errors.phone.message}
              </FormErrorMessage>
            </ContactFormControl>

            {!source && (
              <Flex
                w={"full"}
                justify={"end"}
              >
                <Button
                  fontSize={"xs"}
                  variant={"link"}
                  textTransform={"uppercase"}
                  onClick={onToggle}
                >
                  {isOpen ? t("show_less_fields") : t("show_more_fields")}
                </Button>
              </Flex>
            )}
          </Flex>

          <SlideFade in={isOpen}>
            <Flex
              justifyContent={"space-between"}
              wrap={"wrap"}
              hidden={!isOpen}
            >
              <ContactFormControl
                label={source ? "Email" : t("reach_me_at")}
                isInvalid={!!errors.email}
              >
                <Flex flexDirection={{ base: "column", md: "row" }}>
                  <ContactFormLabel htmlFor={"email"}>
                    {source ? "Email" : t("reach_me_at")}
                  </ContactFormLabel>
                  <ContactInput
                    control={control}
                    name={"email"}
                    id={"email"}
                    placeholder={"john@company.com"}
                    type={"email"}
                    rules={{
                      pattern: {
                        value:
                          // eslint-disable-next-line no-control-regex
                          /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
                        message: t("email_error2"),
                      },
                    }}
                  />
                </Flex>
                <FormErrorMessage>
                  {errors.email && errors.email.message}
                </FormErrorMessage>
              </ContactFormControl>

              <ContactFormControl
                label={t("restaurant_hotel_name")}
                isInvalid={!!errors.business_name}
              >
                <Flex flexDirection={{ base: "column", md: "row" }}>
                  <ContactFormLabel htmlFor={"business_name"}>
                    {t("restaurant_hotel_name")}
                  </ContactFormLabel>
                  <ContactInput
                    control={control}
                    name={"business_name"}
                    id={"business_name"}
                    placeholder={t("papa_johns")}
                    rules={{
                      minLength: {
                        value: 3,
                        message: t("business_name_error2"),
                      },
                    }}
                  />
                </Flex>
                <FormErrorMessage>
                  {errors.business_name && errors.business_name.message}
                </FormErrorMessage>
              </ContactFormControl>

              <ContactFormControl
                label={source ? "Country" : t("select_country")}
                isInvalid={!!errors.country}
              >
                <Flex flexDirection={{ base: "column", md: "row" }}>
                  <ContactFormLabel htmlFor={"country"}>
                    {source ? "Country" : t("select_country")}
                  </ContactFormLabel>
                  <ContactSelect
                    defaultValue={defaultCountry}
                    options={countryList}
                    control={control}
                    name={"country"}
                    id={"country"}
                    onCountryChange={PhonePlaceholderHandler}
                    placeholder={t("select_country")}
                  />
                </Flex>
                <FormErrorMessage>
                  {errors.country && errors.country.message}
                </FormErrorMessage>
              </ContactFormControl>

              <ContactFormControl
                label={t("comments")}
                isInvalid={!!errors.comments}
              >
                <Flex flexDirection={{ base: "column", md: "row" }}>
                  <ContactFormLabel htmlFor={"comments"}>
                    {t("comments")}
                  </ContactFormLabel>
                  <ContactTextArea
                    control={control}
                    name={"comments"}
                    id={"comments"}
                    placeholder={t("comments_placeholder")}
                  />
                </Flex>
                <FormErrorMessage>
                  {errors.comments && errors.comments.message}
                </FormErrorMessage>
              </ContactFormControl>
            </Flex>
          </SlideFade>

          <Flex
            w={"full"}
            justifyContent={"center"}
            alignItems={"center"}
            flexDirection={"column"}
            mt={50}
          >
            {useV2 && (
              <Box
                id={"recaptcha-v2"}
                mt={4}
              />
            )}
            {!useV2 && (
              <Button
                fontSize={"xl"}
                bg={"brand.dark"}
                py={6}
                px={10}
                color={"white"}
                isLoading={isSubmitting || formLoading}
                isDisabled={isSubmitting || formLoading || formError}
                type={"submit"}
                textTransform={"uppercase"}
                _hover={{
                  bg: "brand.secondary",
                }}
              >
                {formError ? t("error") : isOpen ? t("submit") : t("call_me")}
              </Button>
            )}
          </Flex>
        </Box>
      </FramerBox>
    </AnimatePresence>
  )
}

export default ContactForm
